import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import { API_BASE_URL } from '../../config';

const getAdminHeaders = () => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

export default function AdminAllergens() {
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('allergens'); // 'allergens', 'products', 'scans'

    // Data states
    const [categories, setCategories] = useState([]);
    const [terms, setTerms] = useState([]);
    const [products, setProducts] = useState([]);
    const [scans, setScans] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [groups, setGroups] = useState([]);
    const [allIngredients, setAllIngredients] = useState([]);

    // Form states
    const [newTerm, setNewTerm] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [isHiddenSource, setIsHiddenSource] = useState(false);
    const [newBrand, setNewBrand] = useState('');
    const [newProductName, setNewProductName] = useState('');
    const [newIngredients, setNewIngredients] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupType, setNewGroupType] = useState('ALLERGEN_FREE');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [selectedCompensates, setSelectedCompensates] = useState([]);
    const [memberSearch, setMemberSearch] = useState('');
    const [compensateSearch, setCompensateSearch] = useState('');

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [catRes, termRes, prodRes, scanRes, groupRes, dictRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/admin/allergens/categories`, { credentials: 'include', headers: getAdminHeaders() }),
                fetch(`${API_BASE_URL}/api/admin/allergens/terms`, { credentials: 'include', headers: getAdminHeaders() }),
                fetch(`${API_BASE_URL}/api/admin/allergens/products`, { credentials: 'include', headers: getAdminHeaders() }),
                fetch(`${API_BASE_URL}/api/admin/allergens/scans`, { credentials: 'include', headers: getAdminHeaders() }),
                fetch(`${API_BASE_URL}/api/admin/functional-groups`, { credentials: 'include', headers: getAdminHeaders() }),
                fetch(`${API_BASE_URL}/api/admin/dictionary-rescue`, { credentials: 'include', headers: getAdminHeaders() })
            ]);

            if (catRes.ok) {
                const data = await catRes.json();
                setCategories(data.categories || []);
                if (data.categories?.length > 0 && !selectedCategory) {
                    setSelectedCategory(data.categories[0].id);
                }
            }

            if (termRes.ok) {
                const data = await termRes.json();
                setTerms(data.terms || []);
            }

            if (prodRes.ok) {
                const data = await prodRes.json();
                setProducts(data.products || []);
            }

            if (scanRes.ok) {
                const data = await scanRes.json();
                setScans(data.scans || []);
            }

            if (groupRes.ok) {
                const data = await groupRes.json();
                setGroups(data.groups || []);
            }

            if (dictRes.ok) {
                const data = await dictRes.json();
                setAllIngredients(data.items || []);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const addTerm = async () => {
        if (!newTerm.trim() || !selectedCategory) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/allergens/terms`, {
                method: 'POST',
                credentials: 'include',
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    categoryId: selectedCategory,
                    searchTerm: newTerm.toLowerCase().trim(),
                    displayName: newDisplayName.trim() || newTerm.trim(),
                    isHiddenSource
                })
            });
            if (res.ok) {
                setNewTerm('');
                setNewDisplayName('');
                setIsHiddenSource(false);
                fetchData();
            }
        } catch (error) {
            console.error('Add term error:', error);
        }
    };

    const deleteTerm = async (termId) => {
        if (!window.confirm('Bu alerjen maddesini silmek istediğinize emin misiniz?')) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/allergens/terms/${termId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: getAdminHeaders()
            });
            if (res.ok) fetchData();
        } catch (error) {
            console.error('Delete term error:', error);
        }
    };

    const addProduct = async () => {
        if (!newBrand.trim() || !newProductName.trim()) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/allergens/products`, {
                method: 'POST',
                credentials: 'include',
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    brandName: newBrand.trim(),
                    productName: newProductName.trim(),
                    ocrText: newIngredients.trim()
                })
            });
            if (res.ok) {
                setNewBrand('');
                setNewProductName('');
                setNewIngredients('');
                fetchData();
            }
        } catch (error) {
            console.error('Add product error:', error);
        }
    };

    const deleteProduct = async (prodId) => {
        if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/allergens/products/${prodId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: getAdminHeaders()
            });
            if (res.ok) fetchData();
        } catch (error) {
            console.error('Delete product error:', error);
        }
    };

    const addGroup = async () => {
        if (!newGroupName.trim() || !newGroupType) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/functional-groups`, {
                method: 'POST',
                credentials: 'include',
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    name: newGroupName.trim(),
                    type: newGroupType,
                    members: selectedMembers,
                    compensates: selectedCompensates
                })
            });
            if (res.ok) {
                setNewGroupName('');
                setSelectedMembers([]);
                setSelectedCompensates([]);
                fetchData();
            }
        } catch (error) {
            console.error('Add group error:', error);
        }
    };

    const deleteGroup = async (groupId) => {
        if (!window.confirm('Bu fonksiyonel grubu silmek istediğinize emin misiniz?')) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/functional-groups/${groupId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: getAdminHeaders()
            });
            if (res.ok) fetchData();
        } catch (error) {
            console.error('Delete group error:', error);
        }
    };

    const bulkAddByKeyword = (keyword, targetList = 'members') => {
        if (!keyword || keyword.length < 2) return;

        // BBTV LAW ID-01: No Fuzzy Search allowed. Only exact canonicalName match or explicit ID selection.
        const exactMatch = (name) => name.toLowerCase() === keyword.toLowerCase().trim();

        const matches = allIngredients.filter(i => exactMatch(i.canonicalName));

        const newIds = matches.map(m => m.id);

        if (newIds.length === 0) {
            alert(`"${keyword}" ismiyle birebir eşleşen bir ürün Dictionary'de bulunamadı. Lütfen listeden seçiniz.`);
            return;
        }

        if (targetList === 'members') {
            setSelectedMembers(prev => Array.from(new Set([...prev, ...newIds])));
            setMemberSearch('');
        } else {
            setSelectedCompensates(prev => Array.from(new Set([...prev, ...newIds])));
            setCompensateSearch('');
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'bg-red-100 text-red-800 border-red-200';
            case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // Render components
    const renderAllergenTab = () => {
        const filteredTerms = terms.filter(t => t.categoryId === selectedCategory);
        return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Categories Sidebar */}
                <div className="lg:col-span-1">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
                        <div className="px-4 py-4 border-b border-gray-200 dark:border-slate-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Kategoriler</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{categories.length} kategori</p>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-slate-800 max-h-[600px] overflow-y-auto">
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors ${selectedCategory === cat.id
                                        ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600'
                                        : 'hover:bg-gray-50 dark:hover:bg-slate-800 border-l-4 border-transparent'
                                        }`}
                                >
                                    <div>
                                        <p className={`font-medium ${selectedCategory === cat.id ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                                            {cat.categoryName}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {terms.filter(t => t.categoryId === cat.id).length} madde
                                        </p>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-bold rounded-full border ${getSeverityColor(cat.severity)}`}>
                                        {cat.severity === 'critical' ? 'Kritik' : cat.severity === 'high' ? 'Yüksek' : 'Orta'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Terms List & Add Form */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Yeni Alerjen Maddesi Ekle</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Aranacak Terim *</label>
                                <input
                                    type="text" value={newTerm} onChange={(e) => setNewTerm(e.target.value)}
                                    placeholder="örn: buğday, kazeinat"
                                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Görünen Ad</label>
                                <input
                                    type="text" value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)}
                                    placeholder="örn: Buğday"
                                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={isHiddenSource} onChange={(e) => setIsHiddenSource(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-red-600" />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Gizli Kaynak</span>
                            </label>
                            <button onClick={addTerm} disabled={!newTerm.trim()} className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors flex items-center gap-2">
                                <span className="material-symbols-outlined text-[18px]">add</span> Ekle
                            </button>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden min-h-[300px]">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {categories.find(c => c.id === selectedCategory)?.categoryName || 'Maddeler'}
                            </h3>
                            <span className="text-sm text-gray-500">{filteredTerms.length} madde</span>
                        </div>
                        <div className="p-4 flex flex-wrap gap-2">
                            {filteredTerms.length === 0 ? (
                                <p className="text-gray-500 w-full text-center py-8">Henüz madde bulunmuyor.</p>
                            ) : (
                                filteredTerms.map((term) => (
                                    <div key={term.id} className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${term.isHiddenSource ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 text-purple-700' : 'bg-gray-100 dark:bg-slate-800 border-gray-200 text-gray-700 dark:text-gray-300'}`}>
                                        <span className="font-medium">{term.displayName || term.searchTerm}</span>
                                        <button onClick={() => deleteTerm(term.id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-red-600 transition-all"><span className="material-symbols-outlined text-[16px]">close</span></button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderProductTab = () => (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ürün Tanımla</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input type="text" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="Marka (örn: Sarella)" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white" />
                <input type="text" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} placeholder="Ürün Adı" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white" />
            </div>
            <div className="mb-4">
                <textarea
                    value={newIngredients}
                    onChange={(e) => setNewIngredients(e.target.value)}
                    placeholder="Ürün İçeriği / Alerjenleri (Buraya girilen metin OCR başarısız olsa bile analiz için kullanılacaktır)"
                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white min-h-[100px]"
                ></textarea>
            </div>
            <div className="flex justify-end mb-8">
                <button onClick={addProduct} disabled={!newBrand.trim() || !newProductName.trim()} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg flex items-center gap-2">
                    <span className="material-symbols-outlined">save</span> Kaydet
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-800">
                    <thead>
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Marka</th>
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Ürün</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">İşlem</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                        {products.map((p) => (
                            <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                                <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">{p.brandName}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{p.productName}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => deleteProduct(p.id)} className="text-gray-400 hover:text-red-600"><span className="material-symbols-outlined">delete</span></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderScanTab = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Son Taramalar</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-800">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-slate-800/50">
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Ürün / Marka</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Tespit Edilen Riskler</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Tarih</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                            {scans.length === 0 ? (
                                <tr><td colSpan="3" className="px-6 py-8 text-center text-gray-500">Henüz tarama kaydı yok.</td></tr>
                            ) : (
                                scans.map((scan) => (
                                    <tr key={scan.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-bold text-gray-900 dark:text-white">{scan.productName || 'Bilinmeyen Ürün'}</div>
                                            <div className="text-xs text-gray-500">{scan.brandName || 'Markasız'}</div>
                                            {scan.barcode && <div className="text-[10px] bg-gray-100 dark:bg-slate-700 inline-block px-1 rounded mt-1">{scan.barcode}</div>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {scan.risks.length === 0 ? (
                                                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100">Temiz</span>
                                                ) : (
                                                    scan.risks.map((risk) => (
                                                        <div key={risk.id} className="flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-bold text-red-600 uppercase">{risk.riskSeverity}</span>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full border ${getSeverityColor(risk.allergenTerm?.category?.severity)}`}>
                                                                {risk.allergenTerm?.displayName}
                                                            </span>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                            {new Date(scan.scannedAt).toLocaleString('tr-TR')}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderGroupsTab = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Yeni Fonksiyonel Grup Ekle</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Grup Adı</label>
                        <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="örn: Alternatif Sütler" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tip</label>
                        <select value={newGroupType} onChange={(e) => setNewGroupType(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white">
                            <option value="ALLERGEN_FREE">Alerjen İkame (Örn: Süt &rarr; Badem Sütü)</option>
                            <option value="GENERAL_SUB">Genel İkame (Örn: Limon &rarr; Sirke)</option>
                        </select>
                    </div>
                </div>

                {/* Quick Templates Section */}
                <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
                    <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-[16px]">magic_button</span> Hızlı Şablonlar
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { name: 'Ekmek Grubu', key: 'ekmek' },
                            { name: 'Un Grubu', key: 'un' },
                            { name: 'Süt Grubu', key: 'süt' },
                            { name: 'Yağ Grubu', key: 'yağ' },
                            { name: 'Peynir Grubu', key: 'peynir' }
                        ].map(template => (
                            <button
                                key={template.key}
                                onClick={() => {
                                    setNewGroupName(template.name);
                                    bulkAddByKeyword(template.key, 'members');
                                }}
                                className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300 hover:border-red-400 dark:hover:border-red-900 transition-all flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[14px]">add_circle</span>
                                {template.name}
                                <span className="text-[10px] opacity-40">({template.key})</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Members Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Grup Üyeleri (İkameler)</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={memberSearch}
                                onChange={(e) => setMemberSearch(e.target.value)}
                                placeholder="Malzeme ara..."
                                className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg mb-2 text-sm text-gray-900 dark:text-white"
                            />
                            {memberSearch.length >= 2 && (
                                <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                    <div
                                        className="w-full px-4 py-3 text-left bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-black text-xs border-b border-emerald-100 dark:border-emerald-900/30 flex items-center justify-between"
                                    >
                                        <span>"{memberSearch}" İçin Açıkça Bir Öğeyi Tıklayın (ID-01 Kuralı)</span>
                                        <span className="material-symbols-outlined text-[16px]">touch_app</span>
                                    </div>
                                    {(allIngredients || []).filter(i => i.canonicalName.toLowerCase().includes(memberSearch.toLowerCase()) && !selectedMembers.includes(i.id)).map(i => (
                                        <button key={i.id} onClick={() => { setSelectedMembers([...selectedMembers, i.id]); setMemberSearch(''); }} className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sm text-gray-900 dark:text-white">
                                            {i.canonicalName} {i.familyKey && <span className="text-[10px] opacity-40 ml-1">({i.familyKey})</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {selectedMembers.map(id => {
                                const ing = allIngredients.find(i => i.id === id);
                                return (
                                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs">
                                        {ing?.canonicalName || id}
                                        <button onClick={() => setSelectedMembers(selectedMembers.filter(m => m !== id))}><span className="material-symbols-outlined text-[14px]">close</span></button>
                                    </span>
                                );
                            })}
                        </div>
                    </div>

                    {/* Compensates Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Karşılanan Malzemeler (Eksik Olanlar)</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={compensateSearch}
                                onChange={(e) => setCompensateSearch(e.target.value)}
                                placeholder="Malzeme ara..."
                                className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg mb-2 text-sm text-gray-900 dark:text-white"
                            />
                            {compensateSearch.length >= 2 && (
                                <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                    <div
                                        className="w-full px-4 py-3 text-left bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 font-black text-xs border-b border-rose-100 dark:border-rose-900/30 flex items-center justify-between"
                                    >
                                        <span>"{compensateSearch}" İçin Açıkça Bir Öğeyi Tıklayın (ID-01 Kuralı)</span>
                                        <span className="material-symbols-outlined text-[16px]">touch_app</span>
                                    </div>
                                    {(allIngredients || []).filter(i => i.canonicalName.toLowerCase().includes(compensateSearch.toLowerCase()) && !selectedCompensates.includes(i.id)).map(i => (
                                        <button key={i.id} onClick={() => { setSelectedCompensates([...selectedCompensates, i.id]); setCompensateSearch(''); }} className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sm text-gray-900 dark:text-white">
                                            {i.canonicalName} {i.familyKey && <span className="text-[10px] opacity-40 ml-1">({i.familyKey})</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {selectedCompensates.map(id => {
                                const ing = allIngredients.find(i => i.id === id);
                                return (
                                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-rose-100 text-rose-800 rounded text-xs">
                                        {ing?.canonicalName || id}
                                        <button onClick={() => setSelectedCompensates(selectedCompensates.filter(m => m !== id))}><span className="material-symbols-outlined text-[14px]">close</span></button>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button onClick={addGroup} disabled={!newGroupName.trim() || selectedMembers.length === 0} className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg flex items-center gap-2 transition-all">
                        <span className="material-symbols-outlined">group_add</span> Grubu Kaydet
                    </button>
                </div>
            </div >

            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Tanımlı Fonksiyonel Gruplar</h3>
                    <span className="text-sm text-gray-500">{groups.length} grup</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-800">
                        <thead className="bg-gray-50 dark:bg-slate-800/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Grup Adı</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Üyeler</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Karşıladıkları</th>
                                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                            {groups.map((group) => (
                                <tr key={group.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900 dark:text-white">{group.name}</div>
                                        <div className="text-[10px] text-gray-500 uppercase">{group.type}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {(group.ingredients || []).map(ing => (
                                                <span key={ing.id} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] rounded border border-emerald-100">
                                                    {ing.canonicalName}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {(group.canSubstituteFor || []).map(ing => (
                                                <span key={ing.id} className="px-2 py-0.5 bg-rose-50 text-rose-700 text-[10px] rounded border border-rose-100">
                                                    {ing.canonicalName}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button onClick={() => deleteGroup(group.id)} className="text-gray-400 hover:text-red-600 transition-colors">
                                            <span className="material-symbols-outlined">delete</span>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div >
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex font-sans">
            <AdminSidebar mobileSidebarOpen={mobileSidebarOpen} setMobileSidebarOpen={setMobileSidebarOpen} />
            <main className="flex-1 md:pl-[var(--admin-sidebar-width,18rem)] transition-[padding] duration-300">
                <AdminTopBar title="Alerjen & Risk Yönetimi" onToggleMobile={() => setMobileSidebarOpen(!mobileSidebarOpen)} />
                <div className="px-4 sm:px-6 lg:px-8 py-8">
                    {/* Tabs */}
                    <div className="flex gap-4 mb-8 border-b border-gray-200 dark:border-slate-800 overflow-x-auto">
                        {['allergens', 'products', 'scans', 'groups'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`pb-3 px-2 text-sm font-bold transition-all whitespace-nowrap relative ${activeTab === tab ? 'text-red-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                {tab === 'allergens' ? 'Alerjenler' : tab === 'products' ? 'Tanımlı Ürünler' : tab === 'scans' ? 'Taramalar' : 'Fonksiyonel Gruplar'}
                                {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-red-600 rounded-t-full shadow-[0_0_10px_rgba(220,38,38,0.5)]" />}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div>
                    ) : (
                        <div>
                            {activeTab === 'allergens' && renderAllergenTab()}
                            {activeTab === 'products' && renderProductTab()}
                            {activeTab === 'scans' && renderScanTab()}
                            {activeTab === 'groups' && renderGroupsTab()}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
